import { useState, useEffect } from "react";
import { deleteFile, uploadFile, verifyLicenceText } from "../api/driverApi";
import { getAllPlanByAdmin } from "../api/planApi";
import {
  createSubscriptionByAdmin,
  renewSubscriptionByAdmin,
  getDriverSubscriptionStatus,
} from "../api/subscriptionsApi";
import { toast } from "react-toastify";

/* ─── inline styles ─────────────────────────────────────────── */
const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  },
  modal: {
    background: "#fff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "860px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    fontFamily: "'DM Sans', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 28px 16px",
    borderBottom: "1px solid #e9edf2",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 10,
    borderRadius: "16px 16px 0 0",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 20,
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
  },
  closeBtn: {
    border: "none",
    background: "#f1f5f9",
    borderRadius: 8,
    width: 34,
    height: 34,
    cursor: "pointer",
    fontSize: 16,
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  body: { padding: "24px 28px" },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginBottom: 14,
    marginTop: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  divider: { margin: "24px 0", border: "none", borderTop: "1px solid #e9edf2" },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px 20px",
    minWidth: 0,
  },
  spanFull: { gridColumn: "1 / -1", minWidth: 0 },
  field: { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  required: { color: "#ef4444", marginLeft: 2 },
  input: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 14,
    color: "#0f172a",
    background: "#f8fafc",
    outline: "none",
    transition: "border 0.2s",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 14,
    color: "#0f172a",
    background: "#f8fafc",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  inputDisabled: {
    background: "#f1f5f9",
    color: "#94a3b8",
    cursor: "not-allowed",
  },
  addrCard: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    padding: "16px 18px",
    background: "#fafbfc",
  },
  addrTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 12,
    marginTop: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  docCard: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    padding: "14px 16px",
    background: "#fafbfc",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  docLabel: { fontSize: 13, fontWeight: 600, color: "#374151" },
  fileInput: {
    border: "1.5px dashed #cbd5e1",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    color: "#64748b",
    background: "#f8fafc",
    cursor: "pointer",
  },
  imgPreview: {
    width: 90,
    height: 64,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    display: "block",
  },
  // ✅ NEW: clickable thumbnail wrapper with hover "Preview" overlay
  thumbWrap: {
    position: "relative",
    width: 90,
    height: 64,
    borderRadius: 8,
    cursor: "zoom-in",
    overflow: "hidden",
  },
  thumbOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.15s",
  },
  // ✅ NEW: full-screen image lightbox
  lightboxOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: "32px",
    cursor: "zoom-out",
  },
  lightboxBox: {
    position: "relative",
    maxWidth: "92vw",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
  },
  lightboxImg: {
    maxWidth: "92vw",
    maxHeight: "78vh",
    objectFit: "contain",
    borderRadius: 10,
    boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
    background: "#fff",
  },
  lightboxLabel: {
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  lightboxCloseBtn: {
    position: "absolute",
    top: -18,
    right: -18,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: "#fff",
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  },
  lightboxDownload: {
    color: "#fff",
    textDecoration: "underline",
    fontSize: 13,
    fontWeight: 600,
  },
  deleteBtn: {
    fontSize: 12,
    color: "#ef4444",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 6,
    padding: "3px 10px",
    cursor: "pointer",
    marginLeft: 8,
  },
  licenceRow: { display: "flex", alignItems: "flex-end", gap: 10 },
  licenceInput: { flex: 1 },
  verifyBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "9px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  verifiedBadge: {
    fontSize: 12,
    color: "#16a34a",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 6,
    padding: "3px 10px",
    marginTop: 4,
    display: "inline-block",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 28px 20px",
    borderTop: "1px solid #e9edf2",
    position: "sticky",
    bottom: 0,
    background: "#fff",
    borderRadius: "0 0 16px 16px",
  },
  btnOutline: {
    background: "#fff",
    color: "#374151",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    padding: "9px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnPrimary: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "9px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  spinner: {
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    display: "inline-block",
  },
  planCard: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    padding: "16px 18px",
    background: "#fafbfc",
  },
  planGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px 20px",
    minWidth: 0,
  },
  paymentToggle: { display: "flex", gap: 8 },
  planExpiryText: { fontSize: 12, color: "#94a3b8", marginLeft: 10 },
  // ✅ NEW: Date input style
  dateInput: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 14,
    color: "#0f172a",
    background: "#f8fafc",
    outline: "none",
    transition: "border 0.2s",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  // ✅ NEW: Expiry warning styles
  expiryWarning: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    marginTop: 4,
    display: "inline-block",
  },
  expiryExpired: {
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
  },
  expiryExpiring: {
    background: "#fef3c7",
    color: "#92400e",
    border: "1px solid #fde68a",
  },
  expiryValid: {
    background: "#f0fdf4",
    color: "#16a34a",
    border: "1px solid #bbf7d0",
  },
};

const payToggleBtn = (active) => ({
  flex: 1,
  padding: "8px 0",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  border: active ? "2px solid #0f172a" : "1.5px solid #e2e8f0",
  background: active ? "#0f172a" : "#fff",
  color: active ? "#fff" : "#64748b",
  cursor: "pointer",
  transition: "all 0.15s",
});

const PLAN_COLORS = {
  TRAIL: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
  SILVER: { bg: "#f8fafc", border: "#cbd5e1", text: "#475569" },
  GOLD: { bg: "#fffbeb", border: "#fde68a", text: "#b45309" },
  PLATINUM: { bg: "#f5f3ff", border: "#ddd6fe", text: "#6d28d9" },
};

/* ── helpers ── */

// ✅ NEW: Check expiry status
function getExpiryStatus(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = new Date(dateStr);
  expiryDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { status: "expired", label: `Expired ${Math.abs(diffDays)} days ago`, style: S.expiryExpired };
  if (diffDays === 0) return { status: "expired", label: "Expires today!", style: S.expiryExpired };
  if (diffDays <= 30) return { status: "expiring", label: `Expires in ${diffDays} days`, style: S.expiryExpiring };
  return { status: "valid", label: `Valid for ${diffDays} days`, style: S.expiryValid };
}

// ✅ NEW: Format date to YYYY-MM-DD for input
function formatDateForInput(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
}

// ✅ NEW: Capitalize only the first letter, leave the rest as typed
// (so "coimbatore" -> "Coimbatore" while typing, without forcing every word)
function capitalizeFirst(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ✅ NEW: Safely read a display name out of a referredBy* field that may
// either be a populated Mongoose object ({ name, phone, ... }) or just a
// raw ObjectId string (if the backend didn't .populate() it)
function getRefName(ref) {
  if (!ref) return null;
  if (typeof ref === "object") {
    return ref.name || ref.staffName || ref.phone || ref._id || null;
  }
  return ref;
}

function Field({ label, required, children, style }) {
  return (
    <div style={{ ...S.field, ...style }}>
      <label style={S.label}>
        {label}
        {required && <span style={S.required}>*</span>}
      </label>
      {children}
    </div>
  );
}

function DocCard({ label, required, fileType, file, loading, onUpload, onDelete, onPreview }) {
  return (
    <div style={S.docCard}>
      <span style={S.docLabel}>
        {label}
        {required && <span style={S.required}>*</span>}
      </span>
      <input
        type="file"
        accept="image/*"
        disabled={loading}
        style={S.fileInput}
        onChange={(e) => onUpload(e, fileType)}
      />
      {file.url && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="doc-thumb-wrap" style={S.thumbWrap} onClick={() => onPreview(file.url, label)}>
            <img src={file.url} alt={label} style={S.imgPreview} />
            <div className="doc-thumb-overlay" style={S.thumbOverlay}>🔍 Preview</div>
          </div>
          <button
            disabled={loading}
            style={S.deleteBtn}
            onClick={() => onDelete(file.publicId, fileType)}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ✅ NEW: DocCard with Date picker
function DocCardWithDate({ label, required, fileType, file, loading, onUpload, onDelete, onPreview, dateLabel, dateValue, onDateChange }) {
  const expiryStatus = getExpiryStatus(dateValue);

  return (
    <div style={S.docCard}>
      <span style={S.docLabel}>
        {label}
        {required && <span style={S.required}>*</span>}
      </span>
      <input
        type="file"
        accept="image/*"
        disabled={loading}
        style={S.fileInput}
        onChange={(e) => onUpload(e, fileType)}
      />
      {file.url && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="doc-thumb-wrap" style={S.thumbWrap} onClick={() => onPreview(file.url, label)}>
            <img src={file.url} alt={label} style={S.imgPreview} />
            <div className="doc-thumb-overlay" style={S.thumbOverlay}>🔍 Preview</div>
          </div>
          <button
            disabled={loading}
            style={S.deleteBtn}
            onClick={() => onDelete(file.publicId, fileType)}
          >
            Delete
          </button>
        </div>
      )}
      {/* Date picker */}
      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4, display: "block" }}>
          {dateLabel} <span style={S.required}>*</span>
        </label>
        <input
          type="date"
          style={S.dateInput}
          value={formatDateForInput(dateValue)}
          onChange={(e) => onDateChange(e.target.value)}
        />
        {expiryStatus && (
          <span style={{ ...S.expiryWarning, ...expiryStatus.style }}>
            {expiryStatus.status === "expired" ? "⚠️" : expiryStatus.status === "expiring" ? "⏰" : "✅"} {expiryStatus.label}
          </span>
        )}
      </div>
    </div>
  );
}

// ✅ NEW: Full-size image preview lightbox (click thumbnail -> opens this)
function Lightbox({ image, onClose }) {
  if (!image) return null;
  return (
    <div style={S.lightboxOverlay} onClick={onClose}>
      <div style={S.lightboxBox} onClick={(e) => e.stopPropagation()}>
        <button style={S.lightboxCloseBtn} onClick={onClose}>✕</button>
        <img src={image.url} alt={image.label} style={S.lightboxImg} />
        <div style={S.lightboxLabel}>
          <span>{image.label}</span>
          <a
            href={image.url}
            target="_blank"
            rel="noreferrer"
            style={S.lightboxDownload}
            onClick={(e) => e.stopPropagation()}
          >
            Open original ↗
          </a>
        </div>
      </div>
    </div>
  );
}

function AddrGrid({ addr, setAddr, disabled }) {
  return (
    <div style={S.grid2}>
      <div style={S.spanFull}>
        <Field label="Street / Door No / Area" required>
          <input
            autoComplete="new-password"
            name="addr_line1"
            style={{ ...S.input, ...(disabled ? S.inputDisabled : {}) }}
            disabled={disabled}
            value={addr.street}
            onChange={(e) => setAddr({ ...addr, street: e.target.value })}
          />
        </Field>
      </div>
      <Field label="City" required>
        <input
          autoComplete="new-password"
          name="addr_line2"
          style={{ ...S.input, ...(disabled ? S.inputDisabled : {}) }}
          disabled={disabled}
          value={addr.city}
          onChange={(e) => setAddr({ ...addr, city: capitalizeFirst(e.target.value) })}
        />
      </Field>
      <Field label="State" required>
        <input
          autoComplete="new-password"
          name="addr_line3"
          style={{ ...S.input, ...(disabled ? S.inputDisabled : {}) }}
          disabled={disabled}
          value={addr.state}
          onChange={(e) => setAddr({ ...addr, state: capitalizeFirst(e.target.value) })}
        />
      </Field>
      <Field label="Pincode" required>
        <input
          autoComplete="new-password"
          name="addr_line4"
          style={{ ...S.input, ...(disabled ? S.inputDisabled : {}) }}
          disabled={disabled}
          maxLength={6}
          value={addr.pincode}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "");
            setAddr({ ...addr, pincode: val });
          }}
        />
      </Field>
    </div>
  );
}

/* ─── MAIN COMPONENT ────────────────────────────────────────── */
function DriverProfileModal({ driver, onClose, onUpdateStatus, onUpdateDetails, zones = [] }) {
  console.log(driver, "<<< DRIVERRRRRR STATUS");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicleNumber: "",
  });
  const [photo, setPhoto] = useState({ url: "", publicId: "" });
  const [rcBook, setRcBook] = useState({ url: "", publicId: "" });
  
  // ✅ UPDATED: Insurance with end date
  const [insurance, setInsurance] = useState({ url: "", publicId: "" });
  const [insuranceEndDate, setInsuranceEndDate] = useState("");
  
  // ✅ UPDATED: Driving Licence - Front & Back with validity date
  const [drivingLicenceFront, setDrivingLicenceFront] = useState({ url: "", publicId: "" });
  const [drivingLicenceBack, setDrivingLicenceBack] = useState({ url: "", publicId: "" });
  const [drivingLicenceText, setDrivingLicenceText] = useState("");
  const [drivingLicenceValidUpto, setDrivingLicenceValidUpto] = useState("");
  
  const [permanentAddress, setPermanentAddress] = useState({
    street: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [currentAddress, setCurrentAddress] = useState({
    street: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [isLicenceVerified, setIsLicenceVerified] = useState(false);

  // ✅ NEW: full-size image lightbox state — { url, label } or null
  const [previewImage, setPreviewImage] = useState(null);
  const openPreview = (url, label) => setPreviewImage({ url, label });

  // Zone state
  const [zoneId, setZoneId] = useState("");
  const [zoneName, setZoneName] = useState("");
  const [zoneType, setZoneType] = useState("");

  /* plan states */
  const [allPlans, setAllPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedPlanName, setSelectedPlanName] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  // ✅ FIX: tracks whether admin has confirmed the payment for the newly
  // selected plan. Save Details must stay disabled until this is true
  // whenever the plan has changed.
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  /* subscription state from API */
  const [activeSub, setActiveSub] = useState(null);

  // ─── Fetch all plans ─────────────────────────────────────────────────────
  useEffect(() => {
    getAllPlanByAdmin().then((res) => {
      if (res.success) {
        const plans = res.plans || res.data || [];
        setAllPlans(plans);
      }
    });
  }, []);

  // ─── Fetch driver's active subscription ──────────────────────────────────
  useEffect(() => {
    if (!driver) return;
    const mongoId = driver._id;
    if (!mongoId) return;

    getDriverSubscriptionStatus(mongoId).then((res) => {
      if (res.success && res.data) {
        setActiveSub(res.data);
        console.log("📋 Active subscription found:", res.data.status, res.data);
      } else {
        setActiveSub(null);
        console.log("📋 No active subscription");
      }
    });
  }, [driver]);

  useEffect(() => {
    if (sameAsPermanent) setCurrentAddress({ ...permanentAddress });
  }, [sameAsPermanent, permanentAddress]);

  // ─── Populate form when driver changes ───────────────────────────────────
  useEffect(() => {
    if (driver) {
      setFormData({
        name: driver.name || "",
        email: driver.email || "",
        phone: driver.phone || "",
        vehicleNumber: driver.vehicleNumber || "",
      });
      setPhoto({
        url: driver.photo || "",
        publicId: driver.photoPublicId || "",
      });
      setRcBook({
        url: driver.rcBookCopy || "",
        publicId: driver.rcBookPublicId || "",
      });
      
      // ✅ UPDATED: Insurance with end date
      setInsurance({
        url: driver.insuranceCopy || "",
        publicId: driver.insurancePublicId || "",
      });
      setInsuranceEndDate(driver.insuranceEndDate || "");
      
      // ✅ UPDATED: DL Front & Back with validity
      setDrivingLicenceFront({
        url: driver.drivingLicenceFrontCopy || "",
        publicId: driver.drivingLicenceFrontPublicId || "",
      });
      setDrivingLicenceBack({
        url: driver.drivingLicenceBackCopy || "",
        publicId: driver.drivingLicenceBackPublicId || "",
      });
      setDrivingLicenceText(driver.drivingLicenceText || "");
      setDrivingLicenceValidUpto(driver.drivingLicenceValidUpto || "");
      
      setPermanentAddress(
        driver.permanentAddress || { street: "", city: "", state: "", pincode: "" }
      );
      setCurrentAddress(
        driver.currentAddress || { street: "", city: "", state: "", pincode: "" }
      );
      if (driver.drivingLicenceText) setIsLicenceVerified(true);
      setSelectedPlanName(driver.plan || "TRAIL");
      setSelectedPlanId(driver.planId || "");
      setPaymentMode("cash");
      setPaymentConfirmed(false); // ✅ FIX: reset payment confirmation for fresh driver load
      // Zone
      setZoneId(driver.zoneId || "");
      setZoneName(driver.zoneName || "");
      setZoneType(driver.zoneType || "");
    }
  }, [driver]);

  if (!driver) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // ✅ Email must always be stored/typed in lowercase
    setFormData({ ...formData, [name]: name === "email" ? value.toLowerCase() : value });
  };

  // ─── Validations ─────────────────────────────────────────────────────────
  const validateAddress = (addr, label, showToast = true) => {
    const error = (msg) => { if (showToast) toast.error(msg); return false; };
    if (!addr.street.trim()) return error(`${label} - Street required!`);
    if (!addr.city.trim()) return error(`${label} - City required!`);
    if (!addr.state.trim()) return error(`${label} - State required!`);
    if (!addr.pincode || addr.pincode.length !== 6)
      return error(`${label} - Valid 6-digit pincode required!`);
    return true;
  };

  const validateForm = (showToast = true) => {
    const error = (msg) => { if (showToast) toast.error(msg); return false; };
    if (!formData.name.trim()) return error("Name is required!");
    if (!formData.phone.trim()) return error("Phone is required!");
    if (!formData.vehicleNumber.trim()) return error("Vehicle Number is required!");
    // ✅ Email optional, but if entered must be lowercase + valid format
    if (formData.email && !/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(formData.email))
      return error("Enter a valid email address (lowercase)!");
    if (!drivingLicenceText.trim()) return error("Driving Licence text is required!");
    if (!photo.url) return error("Driver photo is required!");
    if (!rcBook.url) return error("RC Book copy is required!");
    if (!insurance.url) return error("Insurance copy is required!");
    if (!insuranceEndDate) return error("Insurance End Date is required!");
    if (!drivingLicenceFront.url) return error("Driving Licence Front copy is required!");
    if (!drivingLicenceBack.url) return error("Driving Licence Back copy is required!");
    if (!drivingLicenceValidUpto) return error("Driving Licence Valid Upto date is required!");
    if (!/^[A-Za-z0-9]{6,15}$/.test(drivingLicenceText))
      return error("Driving Licence must be 6-15 alphanumeric characters!");
    
    // ✅ Check if documents are expired
    const insuranceExpiry = getExpiryStatus(insuranceEndDate);
    const dlExpiry = getExpiryStatus(drivingLicenceValidUpto);
    
    if (insuranceExpiry?.status === "expired") {
      return error("Insurance has expired! Please upload valid insurance.");
    }
    if (dlExpiry?.status === "expired") {
      return error("Driving Licence has expired! Please upload valid licence.");
    }
    
    return true;
  };

  // ─── File upload/delete ──────────────────────────────────────────────────
  const handleUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const res = await uploadFile(file);
      if (res.success) {
        const fileData = { url: res.url, publicId: res.public_id };
        if (type === "photo") setPhoto(fileData);
        else if (type === "rcBook") setRcBook(fileData);
        else if (type === "insurance") setInsurance(fileData);
        else if (type === "drivingLicenceFront") setDrivingLicenceFront(fileData);
        else if (type === "drivingLicenceBack") setDrivingLicenceBack(fileData);
        toast.success("File uploaded successfully!");
      } else toast.error(res.error || "Upload failed!");
    } catch {
      toast.error("Upload failed! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (publicId, type) => {
    if (!publicId) return;
    setLoading(true);
    try {
      const res = await deleteFile(publicId);
      if (res.success) {
        if (type === "photo") setPhoto({ url: "", publicId: "" });
        else if (type === "rcBook") setRcBook({ url: "", publicId: "" });
        else if (type === "insurance") {
          setInsurance({ url: "", publicId: "" });
          setInsuranceEndDate("");
        }
        else if (type === "drivingLicenceFront") setDrivingLicenceFront({ url: "", publicId: "" });
        else if (type === "drivingLicenceBack") setDrivingLicenceBack({ url: "", publicId: "" });
        toast.success("File deleted successfully!");
      } else toast.error(res.error || "Delete failed!");
    } catch {
      toast.error("Delete failed! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Licence verify ─────────────────────────────────────────────────────
  const verifyLicense = async () => {
    if (!drivingLicenceText.trim()) {
      toast.error("Driving Licence text is required!");
      return;
    }
    if (!/^[A-Za-z0-9]{6,15}$/.test(drivingLicenceText)) {
      toast.error("Driving Licence must be 6-15 alphanumeric characters!");
      return;
    }
    setLoading(true);
    try {
      const data = await verifyLicenceText({ drivingLicenceText: drivingLicenceText });
      if (data.success) {
        toast.error("This licence number is already in use!");
        setIsLicenceVerified(false);
      } else {
        toast.success("Licence number is valid and available!");
        setIsLicenceVerified(true);
      }
    } catch {
      toast.error("Something went wrong during verification");
      setIsLicenceVerified(false);
    } finally {
      setLoading(false);
    }
  };

  // ─── Plan change handler ────────────────────────────────────────────────
  const handlePlanChange = (e) => {
    const planId = e.target.value;
    const planObj = allPlans.find((p) => p._id === planId);
    if (planObj) {
      setSelectedPlanId(planObj._id);
      setSelectedPlanName(planObj.name);
    } else {
      setSelectedPlanId("");
      setSelectedPlanName("");
    }
    setPaymentMode("cash");
    setPaymentConfirmed(false); // ✅ FIX: switching plans always requires a fresh payment confirmation
  };

  // ─── Zone change handler ────────────────────────────────────────────────
  const handleZoneChange = (e) => {
    const selectedId = e.target.value;
    const zone = zones.find((z) => z.zoneId === selectedId);
    if (zone) {
      setZoneId(zone.zoneId);
      setZoneName(zone.name);
      setZoneType(zone.type);
    } else {
      setZoneId("");
      setZoneName("");
      setZoneType("");
    }
  };

  // ─── SAVE — Main handler ────────────────────────────────────────────────
  const handleSaveDetails = async () => {
    if (!validateForm(true)) return;
    if (!validateAddress(permanentAddress, "Permanent")) return;
    if (!validateAddress(currentAddress, "Current")) return;

    const driverId = driver.driverId || driver._id;
    const planChanged = selectedPlanId && selectedPlanId !== (driver.planId || "");

    // ✅ FIX: guard against saving a new/changed plan without confirmed payment,
    // even if this ever gets triggered outside the disabled button.
    if (planChanged && !paymentConfirmed) {
      toast.error("Please confirm the plan payment before saving details!");
      return;
    }

    setLoading(true);
    try {
      // ═══ Step 1: If plan changed → call subscription API ═══
      if (planChanged) {
        const payload = {
          driverId: driver._id,
          planId: selectedPlanId,
          method: paymentMode,
        };

        let subRes;

        if (activeSub && (activeSub.status === "active" || activeSub.status === "pending")) {
          console.log("🔄 Renewing subscription:", payload);
          subRes = await renewSubscriptionByAdmin(payload);
        } else {
          console.log("✅ Creating new subscription:", payload);
          subRes = await createSubscriptionByAdmin(payload);
        }

        console.log("📦 Subscription API response:", subRes);

        if (!subRes.success) {
          toast.error(subRes.error || subRes.message || "Subscription failed!");
          setLoading(false);
          return;
        }

        if (paymentMode === "cash") {
          toast.success(
            `✅ ${activeSub?.status === "active" ? "Renewal" : "Subscription"} created! ` +
            `Cash ₹${subRes.data?.amount || ""} — pending admin confirmation.`
          );
        } else {
          toast.success(
            `✅ Razorpay order created! ₹${subRes.data?.amount || ""}. ` +
            `Driver can pay via app.`
          );
        }
      }

      // ═══ Step 2: Update driver details ═══
      const updatedData = {
        ...formData,
        photo: photo.url,
        photoPublicId: photo.publicId,
        rcBookCopy: rcBook.url,
        rcBookPublicId: rcBook.publicId,
        insuranceCopy: insurance.url,
        insurancePublicId: insurance.publicId,
        insuranceEndDate: insuranceEndDate,  
        drivingLicenceFrontCopy: drivingLicenceFront.url,       
        drivingLicenceFrontPublicId: drivingLicenceFront.publicId, 
        drivingLicenceBackCopy: drivingLicenceBack.url,         
        drivingLicenceBackPublicId: drivingLicenceBack.publicId,   
        drivingLicenceText: drivingLicenceText,
        drivingLicenceValidUpto: drivingLicenceValidUpto,    
        permanentAddress,
        currentAddress,
        plan: selectedPlanName,
        planId: selectedPlanId,
        zoneId: zoneId || null,
        zoneName: zoneName || null,
        zoneType: zoneType || null,
        status: driver.status === "applied" ? "waiting" : driver.status === "verification_in_progress" ? "approved" : driver.status,
      };

      await onUpdateDetails(driverId, updatedData);
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Derived values ─────────────────────────────────────────────────────
  const planChanged = selectedPlanId && selectedPlanId !== (driver.planId || "");
  const currentPlanName = activeSub?.planId?.name || driver.plan || "None";
  const currentPlanColor = PLAN_COLORS[currentPlanName] || PLAN_COLORS.TRAIL;
  const selectedPlanObj = allPlans.find((p) => p._id === selectedPlanId);
  const hasActiveSub = activeSub && activeSub.status === "active";

  // ✅ Get expiry status for display
  const insuranceExpiryStatus = getExpiryStatus(insuranceEndDate);
  const dlExpiryStatus = getExpiryStatus(drivingLicenceValidUpto);

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .doc-thumb-wrap:hover .doc-thumb-overlay { opacity: 1; }
      `}</style>

      <Lightbox image={previewImage} onClose={() => setPreviewImage(null)} />

      <div style={S.overlay} onClick={onClose}>
        <div style={S.modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={S.header}>
            <h2 style={S.headerTitle}>
              <span>🛺</span> Driver Profile
            </h2>
            <button style={S.closeBtn} onClick={onClose}>✕</button>
          </div>

          <div style={S.body}>
            {/* ── Section 1: Basic Info ── */}
            <p style={S.sectionTitle}><span>👤</span> Basic Information</p>
            <div style={S.grid2}>
              <Field label="Name" required>
                <input autoComplete="off" name="name" style={S.input}
                  value={formData.name} onChange={handleChange} />
              </Field>
              <Field label="Email">
                <input autoComplete="off" name="email" style={S.input}
                  value={formData.email} onChange={handleChange} />
              </Field>
              <Field label="Phone" required>
                <input autoComplete="off" name="phone" style={S.input}
                  value={formData.phone} onChange={handleChange} />
              </Field>
              <Field label="Vehicle Number" required>
                <input autoComplete="off" name="vehicleNumber" style={S.input}
                  value={formData.vehicleNumber} onChange={handleChange} />
              </Field>
              <div style={S.spanFull}>
                <Field label="Driving Licence Number" required>
                  <div style={S.licenceRow}>
                    <div style={{ ...S.licenceInput, display: "flex", flexDirection: "column", gap: 4 }}>
                      <input autoComplete="off" name="drivingLicenceText" style={S.input}
                        value={drivingLicenceText}
                        onChange={(e) => {
                          let val = e.target.value.toUpperCase();
                          if (val && !/^[A-Z0-9]*$/.test(val)) {
                            toast.error("Licence must contain only capital letters and numbers!");
                            return;
                          }
                          setDrivingLicenceText(val);
                          setIsLicenceVerified(false);
                        }}
                      />
                      {isLicenceVerified && <span style={S.verifiedBadge}>✅ Licence verified</span>}
                    </div>
                    <button style={{ ...S.verifyBtn, opacity: loading ? 0.6 : 1 }}
                      disabled={loading} onClick={verifyLicense}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                        fill="currentColor" viewBox="0 0 16 16">
                        <path d="M16 2L6 12l-4-4 1.5-1.5L6 9l8.5-8.5z" />
                      </svg>
                      Verify
                    </button>
                  </div>
                </Field>
              </div>
            </div>

            <hr style={S.divider} />

            {/* ── Section: Referral & Incentive ── */}
            <p style={S.sectionTitle}><span>💰</span> Referral &amp; Incentive</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {/* Driver's own invite code + earnings balance */}
              <div style={S.addrCard}>
                <p style={S.addrTitle}>🔗 Driver's Referral Code &amp; Balance</p>
                <div>
                  <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>
                    Invite Code
                  </span>
                  <div style={{
                    fontSize: 14, fontWeight: 700, color: "#0f172a", fontFamily: "monospace",
                    background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 6,
                    padding: "6px 10px", marginTop: 4, display: "inline-block",
                  }}>
                    {driver.driverInviteCode || "—"}
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px", marginTop: 12 }}>
                  <div>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Total Earned</span>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#16a34a" }}>
                      ₹{driver.totalReferralIncentive || 0}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Paid Out</span>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#334155" }}>
                      ₹{driver.incentivePaidAmount || 0}
                    </div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Pending Balance</span>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#b45309" }}>
                      ₹{(driver.totalReferralIncentive || 0) - (driver.incentivePaidAmount || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Who referred THIS driver — BDE or another driver */}
              <div style={S.addrCard}>
                <p style={S.addrTitle}>👥 Referred By</p>
                {driver.referredByStaffId ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{
                      fontSize: 11, background: "#dbeafe", color: "#1d4ed8", borderRadius: 4,
                      padding: "2px 8px", fontWeight: 600, width: "fit-content",
                    }}>
                      BDE Referral
                    </span>
                    <div style={{ fontSize: 13, color: "#1e293b" }}>
                      <strong>BDE:</strong> {getRefName(driver.referredByStaffId) || "—"}
                    </div>
                    <div style={{ fontSize: 13, color: "#1e293b" }}>
                      <strong>Invite Code:</strong> {driver.referredByInviteCode || "—"}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {driver.firstRideIncentiveCredited ? (
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>
                          ✅ ₹100 credited
                          {driver.firstRideIncentiveCreditedAt
                            ? ` on ${new Date(driver.firstRideIncentiveCreditedAt).toLocaleDateString("en-IN")}`
                            : ""}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>⏳ First-ride incentive not yet credited</span>
                      )}
                    </div>
                  </div>
                ) : driver.referredByDriverId ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{
                      fontSize: 11, background: "#ede9fe", color: "#6d28d9", borderRadius: 4,
                      padding: "2px 8px", fontWeight: 600, width: "fit-content",
                    }}>
                      Driver Referral
                    </span>
                    <div style={{ fontSize: 13, color: "#1e293b" }}>
                      <strong>Referrer Driver:</strong> {getRefName(driver.referredByDriverId) || "—"}
                    </div>
                    <div style={{ fontSize: 13, color: "#1e293b" }}>
                      <strong>Invite Code:</strong> {driver.referredByDriverInviteCode || "—"}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {driver.driverReferralIncentiveCredited ? (
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>
                          ✅ ₹100 credited
                          {driver.driverReferralIncentiveCreditedAt
                            ? ` on ${new Date(driver.driverReferralIncentiveCreditedAt).toLocaleDateString("en-IN")}`
                            : ""}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>⏳ First-ride incentive not yet credited</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>
                    Direct signup — not referred by anyone
                  </p>
                )}
              </div>
            </div>

            <hr style={S.divider} />

            {/* ── Section: Zone Assignment ── */}
            <p style={S.sectionTitle}><span>🗺️</span> Zone Assignment</p>
            <div style={S.grid2}>
              <div style={{ ...S.field, gridColumn: "1 / -1" }}>
                <label style={S.label}>Assigned Zone</label>
                {zones.length === 0 ? (
                  <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
                    No zones available. Create zones from the Zones page.
                  </p>
                ) : (
                  <select style={S.select} value={zoneId} onChange={handleZoneChange}>
                    <option value="">— No Zone —</option>
                    {zones.map((z) => (
                      <option key={z.zoneId} value={z.zoneId}>
                        [{z.type}] {z.name} · {z.zoneId}
                      </option>
                    ))}
                  </select>
                )}

                {/* Zone info card — show when a zone is selected */}
                {zoneId && (() => {
                  const selZone = zones.find((z) => z.zoneId === zoneId);
                  if (!selZone) return null;
                  return (
                    <div style={{
                      marginTop: 10,
                      background: "#f5f3ff",
                      border: "1.5px solid #ddd6fe",
                      borderRadius: 10,
                      padding: "10px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          Zone Info
                        </span>
                        <span style={{ fontSize: 11, background: "#ede9fe", color: "#6d28d9", borderRadius: 4, padding: "1px 8px", fontWeight: 600 }}>
                          {selZone.type}
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
                        <div>
                          <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Zone Name</span>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1e1b4b" }}>{selZone.name}</div>
                        </div>
                        <div>
                          <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Zone ID</span>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", fontFamily: "monospace" }}>{selZone.zoneId}</div>
                        </div>
                        {selZone.location?.city && (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Location</span>
                            <div style={{ fontSize: 12, color: "#555" }}>
                              {[selZone.location.city, selZone.location.state].filter(Boolean).join(", ")}
                              {selZone.location.pincode && ` — ${selZone.location.pincode}`}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <hr style={S.divider} />

            {/* ── Section 2: Address ── */}
            <p style={S.sectionTitle}><span>📍</span> Address Details</p>
            <label style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 13, fontWeight: 600, color: "#475569",
              background: "#f1f5f9", border: "1.5px solid #e2e8f0",
              borderRadius: 8, padding: "8px 14px", cursor: "pointer",
              marginBottom: 14, userSelect: "none",
            }}>
              <input type="checkbox" checked={sameAsPermanent}
                onChange={() => setSameAsPermanent(!sameAsPermanent)}
                style={{ width: 15, height: 15, accentColor: "#0f172a", cursor: "pointer" }} />
              Same as Permanent Address
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={S.addrCard}>
                <p style={S.addrTitle}>🏠 Permanent Address</p>
                <AddrGrid addr={permanentAddress} setAddr={setPermanentAddress} disabled={false} />
              </div>
              <div style={S.addrCard}>
                <p style={S.addrTitle}>📌 Current Address</p>
                <AddrGrid addr={currentAddress} setAddr={setCurrentAddress} disabled={sameAsPermanent} />
              </div>
            </div>

            <hr style={S.divider} />

            {/* ── Section 3: Plan ── */}
            <p style={S.sectionTitle}><span>📋</span> Plan Details</p>
            <div style={S.planCard}>
              {/* Current plan info */}
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>CURRENT PLAN</span>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: currentPlanColor.bg, border: `1.5px solid ${currentPlanColor.border}`,
                  borderRadius: 6, padding: "3px 12px", fontSize: 13, fontWeight: 700, color: currentPlanColor.text,
                }}>
                  ⭐ {currentPlanName}
                </span>

                {hasActiveSub && (
                  <span style={{
                    fontSize: 11, background: "#dcfce7", color: "#15803d",
                    padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                  }}>ACTIVE</span>
                )}
                {activeSub && activeSub.status === "pending" && (
                  <span style={{
                    fontSize: 11, background: "#fef3c7", color: "#92400e",
                    padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                  }}>PENDING PAYMENT</span>
                )}

                {activeSub?.endDate && (
                  <span style={S.planExpiryText}>
                    Expires: {new Date(activeSub.endDate).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </span>
                )}

                {activeSub?.planId && (
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    (₹{activeSub.planId.amount} / {activeSub.planId.value} {activeSub.planId.unit})
                  </span>
                )}
              </div>

              {/* Plan selector */}
              <div style={S.planGrid}>
                <Field label={hasActiveSub ? "Renew / Upgrade Plan" : "Select Plan"}>
                  <select style={S.select} value={selectedPlanId} onChange={handlePlanChange}>
                    <option value="">-- Select a Plan --</option>
                    {allPlans.length === 0 ? (
                      <option value="" disabled>Loading plans...</option>
                    ) : (
                      allPlans
                        .filter((p) => p.isActive)
                        .map((p) => {
                          const isTrail = p.name === "TRAIL";
                          const isCurrentPlan = p._id === (activeSub?.planId?._id || driver.planId);
                          return (
                            <option
                              key={p._id}
                              value={p._id}
                              disabled={isTrail}
                            >
                              {p.name} — ₹{p.amount ?? 0} / {p.value} {p.unit}
                              {isTrail ? " (Free trial — not selectable)" : ""}
                              {isCurrentPlan ? " ← Current" : ""}
                            </option>
                          );
                        })
                    )}
                  </select>
                </Field>
                <Field label="Plan Amount">
                  <input style={{ ...S.input, ...S.inputDisabled }} disabled
                    value={selectedPlanObj ? `₹${selectedPlanObj.amount ?? 0}` : "—"} />
                </Field>
              </div>

              {/* Payment section — only when plan changed */}
              {planChanged && (
                <div style={{
                  marginTop: 16, padding: "14px 16px", background: "#fff",
                  border: "1.5px solid #fde68a", borderRadius: 10,
                }}>
                  <p style={{
                    fontSize: 12, fontWeight: 700, color: "#92400e",
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    marginTop: 0, marginBottom: 12,
                  }}>
                    💳 Payment Details {hasActiveSub ? "(Renewal)" : "(New Subscription)"}
                  </p>
                  <Field label="Payment Mode" style={{ marginBottom: 12 }}>
                    <div style={S.paymentToggle}>
                      <button type="button" style={payToggleBtn(paymentMode === "cash")}
                        onClick={() => { setPaymentMode("cash"); setPaymentConfirmed(false); }}>
                        💵 Cash
                      </button>
                      <button type="button" style={payToggleBtn(paymentMode === "online")}
                        onClick={() => { setPaymentMode("online"); setPaymentConfirmed(false); }}>
                        📱 Online
                      </button>
                    </div>
                  </Field>
                  {paymentMode === "cash" && (
                    <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                      ✅ Cash payment — subscription will be <strong>pending</strong> until admin confirms in <strong>Pending Cash</strong> page.
                    </p>
                  )}
                  {paymentMode === "online" && (
                    <p style={{ fontSize: 12, color: "#2563eb", margin: 0, background: "#eff6ff", padding: "8px 12px", borderRadius: 6 }}>
                      📱 Razorpay order will be created. Driver pays via app → subscription auto-activates.
                    </p>
                  )}
                  {selectedPlanObj && (
                    <p style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", marginTop: 10, marginBottom: 0 }}>
                      Amount: ₹{selectedPlanObj.amount} for {selectedPlanObj.value} {selectedPlanObj.unit}
                    </p>
                  )}

                  {/* ✅ FIX: Save Details must stay disabled until this is checked */}
                  <label style={{
                    display: "flex", alignItems: "center", gap: 8,
                    marginTop: 12, fontSize: 13, fontWeight: 600,
                    color: paymentConfirmed ? "#15803d" : "#92400e",
                    cursor: "pointer",
                  }}>
                    <input
                      type="checkbox"
                      checked={paymentConfirmed}
                      onChange={(e) => setPaymentConfirmed(e.target.checked)}
                    />
                    {paymentMode === "cash"
                      ? `I confirm ₹${selectedPlanObj?.amount ?? 0} cash amount has been received from the driver`
                      : `I confirm the driver has completed the ₹${selectedPlanObj?.amount ?? 0} online payment`}
                  </label>
                </div>
              )}
            </div>

            <hr style={S.divider} />

            {/* ── Section 4: Documents ── */}
            <p style={S.sectionTitle}><span>📄</span> Documents</p>
            
            {/* ✅ Document Expiry Summary */}
            {(insuranceExpiryStatus || dlExpiryStatus) && (
              <div style={{
                display: "flex",
                gap: 12,
                marginBottom: 16,
                flexWrap: "wrap",
              }}>
                {insuranceExpiryStatus && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 12px",
                    borderRadius: 8,
                    ...insuranceExpiryStatus.style,
                  }}>
                    🛡️ Insurance: {insuranceExpiryStatus.label}
                  </div>
                )}
                {dlExpiryStatus && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 12px",
                    borderRadius: 8,
                    ...dlExpiryStatus.style,
                  }}>
                    🪪 DL: {dlExpiryStatus.label}
                  </div>
                )}
              </div>
            )}
            
            <div style={S.grid2}>
              {/* Driver Photo */}
              <DocCard label="Driver Photo" required fileType="photo"
                file={photo} loading={loading} onUpload={handleUpload} onDelete={handleDelete} onPreview={openPreview} />
              
              {/* RC Book */}
              <DocCard label="RC Book Copy" required fileType="rcBook"
                file={rcBook} loading={loading} onUpload={handleUpload} onDelete={handleDelete} onPreview={openPreview} />
              
              {/* ✅ Insurance with End Date */}
              <DocCardWithDate 
                label="Insurance Copy" 
                required 
                fileType="insurance"
                file={insurance} 
                loading={loading} 
                onUpload={handleUpload} 
                onDelete={handleDelete}
                onPreview={openPreview}
                dateLabel="Insurance End Date"
                dateValue={insuranceEndDate}
                onDateChange={setInsuranceEndDate}
              />
              
              {/* ✅ DL Valid Upto Date (separate field) */}
              <div style={S.docCard}>
                <span style={S.docLabel}>
                  🪪 Driving Licence Validity <span style={S.required}>*</span>
                </span>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4, display: "block" }}>
                    Valid Upto Date
                  </label>
                  <input
                    type="date"
                    style={S.dateInput}
                    value={formatDateForInput(drivingLicenceValidUpto)}
                    onChange={(e) => setDrivingLicenceValidUpto(e.target.value)}
                  />
                  {dlExpiryStatus && (
                    <span style={{ ...S.expiryWarning, ...dlExpiryStatus.style }}>
                      {dlExpiryStatus.status === "expired" ? "⚠️" : dlExpiryStatus.status === "expiring" ? "⏰" : "✅"} {dlExpiryStatus.label}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* ✅ DL Front & Back - Full Width */}
            <div style={{ marginTop: 16 }}>
              <p style={{ ...S.sectionTitle, marginBottom: 12 }}>
                <span>🪪</span> Driving Licence Copies
              </p>
              <div style={S.grid2}>
                {/* DL Front */}
                <DocCard 
                  label="Driving Licence - Front Side" 
                  required 
                  fileType="drivingLicenceFront"
                  file={drivingLicenceFront} 
                  loading={loading} 
                  onUpload={handleUpload} 
                  onDelete={handleDelete} 
                  onPreview={openPreview}
                />
                
                {/* DL Back */}
                <DocCard 
                  label="Driving Licence - Back Side" 
                  required 
                  fileType="drivingLicenceBack"
                  file={drivingLicenceBack} 
                  loading={loading} 
                  onUpload={handleUpload} 
                  onDelete={handleDelete} 
                  onPreview={openPreview}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={S.footer}>
            <button style={S.btnOutline} onClick={onClose}>Close</button>
            <button
              style={{
                ...S.btnPrimary,
                opacity: loading || !isLicenceVerified || (planChanged && !paymentConfirmed) ? 0.5 : 1,
                cursor: loading || !isLicenceVerified || (planChanged && !paymentConfirmed) ? "not-allowed" : "pointer",
              }}
              disabled={loading || !isLicenceVerified || (planChanged && !paymentConfirmed)}
              title={
                !isLicenceVerified
                  ? "Please verify the driving licence first"
                  : (planChanged && !paymentConfirmed)
                  ? "Please confirm the plan payment before saving"
                  : ""
              }
              onClick={handleSaveDetails}
            >
              {loading && <span style={S.spinner} />}
              {loading ? "Saving..." : "Save Details"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default DriverProfileModal;